import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Plus,
  X,
  Save,
  Edit,
  Trash2,
  Zap,
  Filter,
  Users,
  Clock,
  PauseCircle,
  PlayCircle,
  Image,
  Mail,
  Award,
  FileText,
  Lock,
  GitBranch,
} from 'lucide-react';

interface Approver {
  level: number;
  role: string;
  required: boolean;
}

interface Condition {
  type: string;
  operator: string;
  value: string | string[];
}

interface Workflow {
  id: number;
  name: string;
  trigger: string;
  conditions: Condition[];
  approvers: Approver[];
  escalationDays: number;
  active: boolean;
}

interface TriggerType {
  value: string;
  label: string;
  icon: string;
}

interface ConditionType {
  value: string;
  label: string;
  options: string[];
}

interface ApproverRole {
  value: string;
  label: string;
}

interface ApprovalWorkflowsTabProps {
  onSettingsChange: () => void;
}

const ApprovalWorkflowsTab = ({ onSettingsChange }: ApprovalWorkflowsTabProps) => {
  const { t } = useTranslation();

  const [workflows, setWorkflows] = useState<Workflow[]>([
    {
      id: 1,
      name: 'Profile Photo Approval',
      trigger: 'Photo Upload',
      conditions: [
        { type: 'employeeLevel', operator: 'equals', value: 'Executive' },
        { type: 'department', operator: 'in', value: ['Legal', 'Finance'] },
      ],
      approvers: [
        { level: 1, role: 'Direct Manager', required: true },
        { level: 2, role: 'HR Manager', required: true },
      ],
      escalationDays: 3,
      active: true,
    },
    {
      id: 2,
      name: 'Contact Information Update',
      trigger: 'Contact Info Change',
      conditions: [{ type: 'sensitivity', operator: 'equals', value: 'High' }],
      approvers: [{ level: 1, role: 'HR Administrator', required: true }],
      escalationDays: 2,
      active: true,
    },
    {
      id: 3,
      name: 'Skills & Certifications',
      trigger: 'Skills Update',
      conditions: [{ type: 'contentType', operator: 'equals', value: 'Certification' }],
      approvers: [{ level: 1, role: 'Direct Manager', required: true }],
      escalationDays: 5,
      active: false,
    },
  ]);

  const [isCreatingWorkflow, setIsCreatingWorkflow] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState<Workflow | null>(null);

  const triggerTypes: TriggerType[] = [
    { value: 'Photo Upload', label: 'Profile Photo Upload', icon: 'Image' },
    { value: 'Contact Info Change', label: 'Contact Information Change', icon: 'Mail' },
    { value: 'Skills Update', label: 'Skills & Expertise Update', icon: 'Award' },
    { value: 'Bio Change', label: 'Professional Bio Change', icon: 'FileText' },
    { value: 'Privacy Change', label: 'Privacy Settings Change', icon: 'Lock' },
  ];

  const conditionTypes: ConditionType[] = [
    { value: 'employeeLevel', label: 'Employee Level', options: ['Junior', 'Mid-Level', 'Senior', 'Executive'] },
    { value: 'department', label: 'Department', options: ['Engineering', 'Finance', 'Legal', 'HR', 'Sales'] },
    { value: 'sensitivity', label: 'Content Sensitivity', options: ['Low', 'Medium', 'High'] },
    { value: 'contentType', label: 'Content Type', options: ['Text', 'Image', 'Certification', 'Link'] },
  ];

  const approverRoles: ApproverRole[] = [
    { value: 'Direct Manager', label: 'Direct Manager' },
    { value: 'HR Manager', label: 'HR Manager' },
    { value: 'HR Administrator', label: 'HR Administrator' },
    { value: 'Department Head', label: 'Department Head' },
    { value: 'System Administrator', label: 'System Administrator' },
  ];

  const handleToggleWorkflow = (workflowId: number) => {
    setWorkflows((prev) => prev.map((w) => (w.id === workflowId ? { ...w, active: !w.active } : w)));
    onSettingsChange();
  };

  const handleDeleteWorkflow = (workflowId: number) => {
    if (window.confirm('Are you sure you want to delete this workflow?')) {
      setWorkflows((prev) => prev.filter((w) => w.id !== workflowId));
      onSettingsChange();
    }
  };

  const handleEditWorkflow = (workflow: Workflow) => {
    setSelectedWorkflow(workflow);
    setIsCreatingWorkflow(true);
  };

  const getTriggerIcon = (iconName: string) => {
    switch (iconName) {
      case 'Image':
        return <Image className="w-4 h-4 text-gray-600 mr-2" />;
      case 'Mail':
        return <Mail className="w-4 h-4 text-gray-600 mr-2" />;
      case 'Award':
        return <Award className="w-4 h-4 text-gray-600 mr-2" />;
      case 'FileText':
        return <FileText className="w-4 h-4 text-gray-600 mr-2" />;
      case 'Lock':
        return <Lock className="w-4 h-4 text-gray-600 mr-2" />;
      default:
        return <Image className="w-4 h-4 text-gray-600 mr-2" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Actions */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{t('Approval Workflows')}</h3>
          <p className="text-sm text-gray-600 mt-1">
            {t('Configure automated approval processes for profile changes based on conditions')}
          </p>
        </div>
        <button
          className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          onClick={() => setIsCreatingWorkflow(true)}
        >
          <Plus className="w-4 h-4" />
          {t('Create Workflow')}
        </button>
      </div>

      {/* Workflow Builder (shown when creating/editing) */}
      {isCreatingWorkflow && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-sm font-semibold text-blue-900">{t('Workflow Builder')}</h4>
            <button
              className="p-1 text-gray-700 hover:text-gray-900"
              onClick={() => {
                setIsCreatingWorkflow(false);
                setSelectedWorkflow(null);
              }}
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="space-y-4">
            {/* Workflow Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('Workflow Name')}</label>
              <input
                type="text"
                placeholder={t('Enter workflow name')}
                className="w-full px-3 py-2 border border-border rounded-lg focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                defaultValue={selectedWorkflow?.name}
              />
            </div>

            {/* Trigger Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('Trigger Event')}</label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {triggerTypes.map((trigger) => (
                  <button
                    key={trigger.value}
                    className="flex items-center p-3 border border-border rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
                  >
                    {getTriggerIcon(trigger.icon)}
                    <span className="text-sm text-gray-900">{t(trigger.label)}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Conditions */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">{t('Conditions')}</label>
                <button className="flex items-center gap-1 px-2 py-1 text-xs border border-border rounded text-gray-700 hover:bg-gray-50">
                  <Plus className="w-3 h-3" />
                  {t('Add Condition')}
                </button>
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2 p-3 bg-card border border-border rounded-lg">
                  <select className="flex-1 px-3 py-1.5 border border-border rounded text-sm">
                    {conditionTypes.map((ct) => (
                      <option key={ct.value} value={ct.value}>
                        {t(ct.label)}
                      </option>
                    ))}
                  </select>
                  <select className="px-3 py-1.5 border border-border rounded text-sm">
                    <option value="equals">{t('Equals')}</option>
                    <option value="in">{t('In')}</option>
                    <option value="not_equals">{t('Not Equals')}</option>
                  </select>
                  <input
                    type="text"
                    placeholder={t('Value')}
                    className="flex-1 px-3 py-1.5 border border-border rounded text-sm"
                  />
                  <button className="p-1 text-gray-700 hover:text-gray-900">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Approval Levels */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">{t('Approval Levels')}</label>
                <button className="flex items-center gap-1 px-2 py-1 text-xs border border-border rounded text-gray-700 hover:bg-gray-50">
                  <Plus className="w-3 h-3" />
                  {t('Add Level')}
                </button>
              </div>
              <div className="space-y-2">
                {[1, 2].map((level) => (
                  <div key={level} className="flex items-center gap-2 p-3 bg-card border border-border rounded-lg">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                      <span className="text-sm font-semibold text-blue-600">{level}</span>
                    </div>
                    <select className="flex-1 px-3 py-1.5 border border-border rounded text-sm">
                      {approverRoles.map((role) => (
                        <option key={role.value} value={role.value}>
                          {t(role.label)}
                        </option>
                      ))}
                    </select>
                    <label className="flex items-center text-sm text-gray-600">
                      <input type="checkbox" className="mr-1.5" defaultChecked />
                      {t('Required')}
                    </label>
                    <button className="p-1 text-gray-700 hover:text-gray-900">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Escalation Settings */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('Escalation Days')}</label>
              <input
                type="number"
                placeholder={t('Days before escalation')}
                className="w-full px-3 py-2 border border-border rounded-lg focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                defaultValue={selectedWorkflow?.escalationDays || 3}
              />
              <p className="text-xs text-gray-500 mt-1">{t('Number of days before escalating to next approver')}</p>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-2">
              <button
                className="px-3 py-1.5 text-sm border border-border rounded-lg text-gray-700 hover:bg-gray-50"
                onClick={() => {
                  setIsCreatingWorkflow(false);
                  setSelectedWorkflow(null);
                }}
              >
                {t('Cancel')}
              </button>
              <button className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600">
                <Save className="w-4 h-4" />
                {t('Save Workflow')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Existing Workflows List */}
      <div className="space-y-3">
        {workflows.map((workflow) => (
          <div
            key={workflow.id}
            className={`bg-white border rounded-lg p-4 transition-all duration-200 ${
              workflow.active ? 'border-border' : 'border-border opacity-60'
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h4 className="text-sm font-semibold text-gray-900">{t(workflow.name)}</h4>
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      workflow.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {workflow.active ? t('Active') : t('Inactive')}
                  </span>
                </div>

                <div className="space-y-2">
                  {/* Trigger */}
                  <div className="flex items-center text-sm text-gray-600">
                    <Zap className="w-3.5 h-3.5 mr-2" />
                    <span className="font-medium">{t('Trigger')}:</span>
                    <span className="ml-1">{t(workflow.trigger)}</span>
                  </div>

                  {/* Conditions */}
                  <div className="flex items-start text-sm text-gray-600">
                    <Filter className="w-3.5 h-3.5 mr-2 mt-0.5" />
                    <div>
                      <span className="font-medium">{t('Conditions')}:</span>
                      <div className="ml-1 inline">
                        {workflow.conditions.map((cond, idx) => (
                          <span key={idx} className="ml-1 text-xs bg-gray-100 px-2 py-0.5 rounded">
                            {cond.type} {cond.operator} {Array.isArray(cond.value) ? cond.value.join(', ') : cond.value}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Approvers */}
                  <div className="flex items-start text-sm text-gray-600">
                    <Users className="w-3.5 h-3.5 mr-2 mt-0.5" />
                    <div>
                      <span className="font-medium">{t('Approvers')}:</span>
                      <div className="ml-1 inline">
                        {workflow.approvers.map((approver, idx) => (
                          <span key={idx} className="ml-1 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                            L{approver.level}: {t(approver.role)}
                            {approver.required && ' *'}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Escalation */}
                  <div className="flex items-center text-sm text-gray-600">
                    <Clock className="w-3.5 h-3.5 mr-2" />
                    <span className="font-medium">{t('Escalation')}:</span>
                    <span className="ml-1">
                      {workflow.escalationDays} {t('days')}
                    </span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 ml-4">
                <button className="p-1 text-gray-700 hover:text-gray-900" onClick={() => handleEditWorkflow(workflow)}>
                  <Edit className="w-4 h-4" />
                </button>
                <button className="p-1 text-gray-700 hover:text-gray-900" onClick={() => handleToggleWorkflow(workflow.id)}>
                  {workflow.active ? <PauseCircle className="w-4 h-4" /> : <PlayCircle className="w-4 h-4" />}
                </button>
                <button className="p-1 text-gray-700 hover:text-gray-900" onClick={() => handleDeleteWorkflow(workflow.id)}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Workflow Statistics */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h4 className="text-sm font-semibold text-gray-900 mb-4">{t('Workflow Statistics')}</h4>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <p className="text-2xl font-bold text-blue-600">12</p>
            <p className="text-xs text-gray-600 mt-1">{t('Pending Approvals')}</p>
          </div>
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <p className="text-2xl font-bold text-green-600">48</p>
            <p className="text-xs text-gray-600 mt-1">{t('Approved This Month')}</p>
          </div>
          <div className="text-center p-4 bg-red-50 rounded-lg">
            <p className="text-2xl font-bold text-red-600">3</p>
            <p className="text-xs text-gray-600 mt-1">{t('Rejected')}</p>
          </div>
          <div className="text-center p-4 bg-amber-50 rounded-lg">
            <p className="text-2xl font-bold text-amber-600">2.5</p>
            <p className="text-xs text-gray-600 mt-1">{t('Avg Days to Approve')}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApprovalWorkflowsTab;
